import { useState } from 'react';
import { paymentService } from '../../../services/paymentService';
import { toastHelper } from '../../../utils/toastHelper';
import { UpgradeProration } from './useSubscriptionData';

export interface SubscriptionActionsViewModel {
  // Loading states
  freeUpgradeLoading: boolean;
  downgradeLoading: string | null;
  upgradeLoading: string | null;
  
  // Modal states
  showDowngradeModal: boolean;
  setShowDowngradeModal: (show: boolean) => void;
  pendingDowngradePlanId: string | null;
  setPendingDowngradePlanId: (planId: string | null) => void;
  showUpgradeModal: boolean;
  setShowUpgradeModal: (show: boolean) => void;
  pendingUpgradePlanId: string | null;
  setPendingUpgradePlanId: (planId: string | null) => void;
  
  // Proration state
  showProrationFormula: boolean;
  setShowProrationFormula: (show: boolean) => void;
  prorationWizardStep: number;
  setProrationWizardStep: (step: number) => void;
  upgradeProration: UpgradeProration | null;
  setUpgradeProration: (proration: UpgradeProration | null) => void;
  
  // Actions
  handleFreeUpgrade: () => Promise<void>;
  handleDowngradeClick: (newPlanId: string) => void;
  handleDowngradeConfirm: () => Promise<void>;
  handleUpgrade: (newPlanId: string) => Promise<void>;
  handleUpgradeConfirm: () => Promise<void>;
}

export function useSubscriptionActions(
  hasPendingDowngrade: boolean,
  subscriptionStatus: any,
  fetchSubscriptionData: () => Promise<void>
): SubscriptionActionsViewModel {
  const [freeUpgradeLoading, setFreeUpgradeLoading] = useState<boolean>(false);
  const [downgradeLoading, setDowngradeLoading] = useState<string | null>(null);
  const [upgradeLoading, setUpgradeLoading] = useState<string | null>(null);
  const [showDowngradeModal, setShowDowngradeModal] = useState<boolean>(false);
  const [pendingDowngradePlanId, setPendingDowngradePlanId] = useState<string | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState<boolean>(false);
  const [pendingUpgradePlanId, setPendingUpgradePlanId] = useState<string | null>(null);
  const [showProrationFormula, setShowProrationFormula] = useState<boolean>(false);
  const [prorationWizardStep, setProrationWizardStep] = useState<number>(1);
  const [upgradeProration, setUpgradeProration] = useState<UpgradeProration | null>(null);

  // Handle free upgrade
  const handleFreeUpgrade = async () => {
    // Note: We no longer block upgrades when there's a pending cancellation/downgrade
    // The backend will handle reactivation if needed

    try {
      setFreeUpgradeLoading(true);
      const result = await paymentService.freeUpgradeToPro();
      
      if (result.approval_url) {
        // Redirect main window to PayPal approval URL
        window.location.href = result.approval_url;
      } else {
        // No approval needed
        toastHelper.newMessage('success', 'Success', 'Free upgrade to Pro initiated');
        setFreeUpgradeLoading(false);
        // Refresh subscription data
        await fetchSubscriptionData();
      }
    } catch (error: any) {
      toastHelper.newMessage(
        'error',
        'Error',
        error.response?.data?.message || 'Failed to initiate free upgrade'
      );
      setFreeUpgradeLoading(false);
    }
  };

  // Handle downgrade click - show confirmation modal
  const handleDowngradeClick = (newPlanId: string) => {
    setPendingDowngradePlanId(newPlanId);
    setShowDowngradeModal(true);
  };

  // Handle downgrade confirmation - proceed with downgrade
  const handleDowngradeConfirm = async () => {
    if (!pendingDowngradePlanId) return;
    
    setShowDowngradeModal(false);
    const newPlanId = pendingDowngradePlanId;
    setPendingDowngradePlanId(null);
    
    try {
      setDowngradeLoading(newPlanId);
      const result = await paymentService.downgradeSubscription(newPlanId);
      
      if (result.approval_url) {
        // Redirect main window to PayPal approval URL
        window.location.href = result.approval_url;
      } else {
        // No approval needed
        toastHelper.newMessage('success', 'Success', result.message || 'Downgrade initiated');
        setDowngradeLoading(null);
        // Refresh subscription data
        await fetchSubscriptionData();
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to initiate downgrade. Please try again later.';
      toastHelper.newMessage(
        'error',
        'Downgrade Failed',
        errorMessage
      );
      setDowngradeLoading(null);
    }
  };

  // Handle upgrade click - show modal with proration info
  const handleUpgrade = async (newPlanId: string) => {
    try {
      // Note: We no longer block upgrades when there's a pending cancellation/downgrade
      // The backend will handle reactivation if needed and clear the pending state

      // Get proration information first (don't set loading state yet)
      const proration = await paymentService.getUpgradeProration(newPlanId);
      setUpgradeProration(proration);
      setPendingUpgradePlanId(newPlanId);
      setShowUpgradeModal(true);
    } catch (error: any) {
      toastHelper.newMessage(
        'error',
        'Error',
        error.response?.data?.message || error.response?.data?.detail || 'Failed to calculate upgrade cost'
      );
    }
  };

  // Handle upgrade confirmation - actually process the upgrade
  const handleUpgradeConfirm = async () => {
    if (!pendingUpgradePlanId) return;

    setShowUpgradeModal(false);

    try {
      setUpgradeLoading(pendingUpgradePlanId);

      const result = await paymentService.upgradeSubscription(pendingUpgradePlanId);

      // If proration is required, redirect to PayPal
      if (result.requires_proration && result.proration_approval_url) {
        // Redirect main window to PayPal for proration payment
        window.location.href = result.proration_approval_url;
        return;
      }

      // If subscription approval is required, redirect to PayPal
      if (result.approval_url) {
        // Redirect main window to PayPal for subscription approval
        window.location.href = result.approval_url;
      } else {
        // No approval needed
        toastHelper.newMessage('success', 'Success', 'Upgrade completed!');
        setUpgradeLoading(null);
        setPendingUpgradePlanId(null);
        setUpgradeProration(null);
        // Reload page to show updated subscription
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (error: any) {
      toastHelper.newMessage(
        'error',
        'Error',
        error.response?.data?.message || 'Failed to initiate upgrade'
      );
      setUpgradeLoading(null);
      setPendingUpgradePlanId(null);
      setUpgradeProration(null);
    }
  };

  return {
    freeUpgradeLoading,
    downgradeLoading,
    upgradeLoading,
    showDowngradeModal,
    setShowDowngradeModal,
    pendingDowngradePlanId,
    setPendingDowngradePlanId,
    showUpgradeModal,
    setShowUpgradeModal,
    pendingUpgradePlanId,
    setPendingUpgradePlanId,
    showProrationFormula,
    setShowProrationFormula,
    prorationWizardStep,
    setProrationWizardStep,
    upgradeProration,
    setUpgradeProration,
    handleFreeUpgrade,
    handleDowngradeClick,
    handleDowngradeConfirm,
    handleUpgrade,
    handleUpgradeConfirm,
  };
}
